import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, ViewEncapsulation } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subscription } from 'rxjs'
import {
  NodeConfigDialogComponent,
  NodeConfigDialogResult,
} from 'src/app/core/ddc-canvas/node-config-dialog/node-config-dialog.component'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Themes } from '../../enums/enums'
import { DdcAddMobileSheetComponent } from '../ddc-add-mobile-sheet/ddc-add-mobile-sheet.component'
import { DdcEditMobileSheetComponent } from '../ddc-edit-mobile-sheet/ddc-edit-mobile-sheet.component'
import { DdcFilterMobileSheetComponent } from '../ddc-filter-mobile-sheet/ddc-filter-mobile-sheet.component'
import { DdcGeneratePdfMobileSheetComponent } from '../ddc-generate-pdf-mobile-sheet/ddc-generate-pdf-mobile-sheet.component'
import { DdcReadFigureMobileSheetComponent } from '../ddc-read-figure-mobile-sheet/ddc-read-figure-mobile-sheet.component'
import { DdcReadNoteMobileSheetComponent } from '../ddc-read-note-mobile-sheet/ddc-read-note-mobile-sheet.component'
import { DdcReadPairMobileSheetComponent } from '../ddc-read-pair-mobile-sheet/ddc-read-pair-mobile-sheet.component'
import { DdcReadQuizMobileSheetComponent } from '../ddc-read-quiz-mobile-sheet/ddc-read-quiz-mobile-sheet.component'
import { DdcReadWordMobileSheetComponent } from '../ddc-read-word-mobile-sheet/ddc-read-word-mobile-sheet.component'
import { DdcUpgradeMobileSheetComponent } from '../ddc-upgrade-mobile-sheet/ddc-upgrade-mobile-sheet.component'

@Component({
  selector: 'ddc-desktop-dialog',
  template: `
    <div
      class="ddc-desktop-dialog-container"
      [ngClass]="{
        'dark-theme-desktop-dialog': _isDarkTheme,
        'light-theme-desktop-dialog': _isLightTheme,
        'paper-theme-desktop-dialog': _isPaperTheme,
      }"
    >
      <ng-container [ngSwitch]="data.type">
        <ddc-add-mobile-sheet
          *ngSwitchCase="'add-to-dic'"
          [target_dic]="data.element"
        ></ddc-add-mobile-sheet>
        <ddc-read-word-mobile-sheet
          *ngSwitchCase="'read-word'"
          [word]="data.element"
        ></ddc-read-word-mobile-sheet>
        <ddc-read-pair-mobile-sheet
          *ngSwitchCase="'read-pair'"
          [pair]="data.element"
        ></ddc-read-pair-mobile-sheet>
        <ddc-read-note-mobile-sheet
          *ngSwitchCase="'read-note'"
          [note]="data.element"
        ></ddc-read-note-mobile-sheet>
        <ddc-read-quiz-mobile-sheet
          *ngSwitchCase="'read-quiz'"
          [quiz]="data.element"
        ></ddc-read-quiz-mobile-sheet>
        <ddc-read-figure-mobile-sheet
          *ngSwitchCase="'read-figure'"
          [figure]="data.element"
        ></ddc-read-figure-mobile-sheet>
        <ddc-edit-mobile-sheet
          *ngSwitchCase="'edit-word'"
          [word]="data.element"
        ></ddc-edit-mobile-sheet>
        <ddc-edit-mobile-sheet
          *ngSwitchCase="'edit-pair'"
          [pair]="data.element"
        ></ddc-edit-mobile-sheet>
        <ddc-edit-mobile-sheet
          *ngSwitchCase="'edit-dic'"
          [dictionary]="data.element"
        ></ddc-edit-mobile-sheet>
        <ddc-edit-mobile-sheet
          *ngSwitchCase="'edit-collection'"
          [collection]="data.element"
        ></ddc-edit-mobile-sheet>
        <ddc-filter-mobile-sheet
          *ngSwitchCase="'filter'"
          [type]="data.element"
        ></ddc-filter-mobile-sheet>
        <ddc-upgrade-mobile-sheet
          *ngSwitchCase="'upgrade'"
          [word]="data.element"
        ></ddc-upgrade-mobile-sheet>
        <ddc-generate-pdf-mobile-sheet
          *ngSwitchCase="'generate_pdf'"
          [dictionary]="data.element"
        ></ddc-generate-pdf-mobile-sheet>

        <node-config-dialog
          *ngSwitchCase="'configure'"
          [data]="data.element"
          (cancelled)="onCancelled()"
          (configured)="onConfigured($event)"
        ></node-config-dialog>

        <!-- Loading dialog content -->
        <div class="loading-dialog-content" *ngSwitchCase="'loading'">
          <mat-spinner class="loading-spinner" [diameter]="40"></mat-spinner>
          <p class="loading-message">{{ data.message || 'Loading...' }}</p>
          <p class="loading-submessage" *ngIf="data.submessage">{{ data.submessage }}</p>
        </div>

        <div *ngSwitchDefault>Unknown dialog type</div>
      </ng-container>
    </div>
  `,
  styleUrls: ['./ddc-desktop-dialog.component.scss'],
  imports: [
    CommonModule,
    AngularMaterialsModules,
    DdcReadPairMobileSheetComponent,
    DdcReadWordMobileSheetComponent,
    DdcReadNoteMobileSheetComponent,
    DdcReadQuizMobileSheetComponent,
    DdcReadFigureMobileSheetComponent,
    DdcEditMobileSheetComponent,
    DdcAddMobileSheetComponent,
    DdcFilterMobileSheetComponent,
    DdcUpgradeMobileSheetComponent,
    DdcReadQuizMobileSheetComponent,
    DdcReadFigureMobileSheetComponent,
    DdcGeneratePdfMobileSheetComponent,
    NodeConfigDialogComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class DdcDesktopDialogComponent {
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string
  private themeSubscription: Subscription

  constructor(
    public themeService: ThemeService,
    private dialogRef: MatDialogRef<DdcDesktopDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })
  }

  onConfigured(result: NodeConfigDialogResult): void {
    this.dialogRef.close(result)
  }

  onCancelled(): void {
    this.dialogRef.close()
  }
}
